import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CreditCard, Loader2, Mail, Plus, RefreshCw, Tag, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [subTab, setSubTab] = useState('promo-codes');

  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(true);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10, min_order: 0, max_uses: 0, one_per_customer: false, expires_at: '', is_active: true
  });
  const [promoSaving, setPromoSaving] = useState(false);

  // Square config state
  const [squareConfig, setSquareConfig] = useState(null);
  const [squareToggling, setSquareToggling] = useState(false);

  // Checkout emails state
  const [checkoutEmails, setCheckoutEmails] = useState([]);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [emailsPage, setEmailsPage] = useState(1);

  // Bulk blast state
  const [showBlastForm, setShowBlastForm] = useState(false);
  const [blastForm, setBlastForm] = useState({ promo_code: '', discount_text: '' });
  const [blastSending, setBlastSending] = useState(false);

  const fetchPromoCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, { headers });
      setPromoCodes(res.data);
    } catch {} finally { setPromoLoading(false); }
  }, [token]);

  const fetchSquareConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/square-config`, { headers });
      setSquareConfig(res.data);
    } catch {}
  }, [token]);

  const fetchCheckoutEmails = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/checkout-emails?page=${page}&limit=50`, { headers });
      setCheckoutEmails(res.data.emails);
      setEmailsTotal(res.data.total);
    } catch {} finally { setEmailsLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchPromoCodes();
    fetchSquareConfig();
    fetchCheckoutEmails();
  }, [fetchPromoCodes, fetchSquareConfig, fetchCheckoutEmails]);

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    if (!promoForm.code.trim()) return;
    setPromoSaving(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        ...promoForm,
        code: promoForm.code.trim().toUpperCase(),
        discount_value: parseFloat(promoForm.discount_value) || 0,
        min_order: parseFloat(promoForm.min_order) || 0,
        max_uses: parseInt(promoForm.max_uses) || 0,
        expires_at: promoForm.expires_at || null
      }, { headers });
      toast.success(`Promo code "${promoForm.code.toUpperCase()}" created!`);
      setShowPromoForm(false);
      setPromoForm({ code: '', discount_type: 'percentage', discount_value: 10, min_order: 0, max_uses: 0, one_per_customer: false, expires_at: '', is_active: true });
      fetchPromoCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create promo code');
    } finally { setPromoSaving(false); }
  };

  const togglePromoActive = async (id, currentActive) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${id}`, { is_active: !currentActive }, { headers });
      fetchPromoCodes();
    } catch { toast.error('Failed to update'); }
  };

  const deletePromo = async (id) => {
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { headers });
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleSquareEnv = async () => {
    if (!squareConfig) return;
    const newEnv = squareConfig.environment === 'production' ? 'sandbox' : 'production';
    setSquareToggling(true);
    try {
      const res = await axios.put(`${API}/admin/square-config`, { environment: newEnv }, { headers });
      setSquareConfig(res.data);
      toast.success(`Square switched to ${newEnv}`);
    } catch { toast.error('Failed to toggle'); }
    finally { setSquareToggling(false); }
  };

  const handleSendBlast = async () => {
    if (!blastForm.promo_code || !blastForm.discount_text) {
      toast.error('Please fill in both fields');
      return;
    }
    setBlastSending(true);
    try {
      const res = await axios.post(`${API}/admin/send-promo-blast`, blastForm, { headers });
      toast.success(`Promo blast sent to ${res.data.sent} recipients!`);
      setShowBlastForm(false);
      setBlastForm({ promo_code: '', discount_text: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send blast');
    } finally { setBlastSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-settings">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Settings</h2>
        <p className="text-sm text-gray-500">Promo codes, payment config, and checkout data</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'promo-codes', label: 'Promo Codes' },
          { id: 'payments', label: 'Payments' },
          { id: 'checkout-emails', label: 'Checkout Emails' }
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded transition-colors ${subTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid={`settings-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── Promo Codes ─── */}
      {subTab === 'promo-codes' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">{promoCodes.length} promo code{promoCodes.length !== 1 ? 's' : ''}</p>
            <button onClick={() => setShowPromoForm(!showPromoForm)}
              className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 flex items-center gap-2"
              data-testid="new-promo-btn">
              <Plus className="w-4 h-4" /> New Code
            </button>
          </div>

          {showPromoForm && (
            <form onSubmit={handleCreatePromo} className="bg-white border border-gray-200 rounded-lg p-5 space-y-4" data-testid="promo-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input value={promoForm.code} onChange={(e) => setPromoForm({...promoForm, code: e.target.value.toUpperCase()})}
                    placeholder="SAVE10" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase" required data-testid="promo-code-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={promoForm.discount_type} onChange={(e) => setPromoForm({...promoForm, discount_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="promo-type-select">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {promoForm.discount_type === 'percentage' ? 'Discount %' : 'Discount $'}
                  </label>
                  <input type="number" step="0.01" min="0" value={promoForm.discount_value}
                    onChange={(e) => setPromoForm({...promoForm, discount_value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="promo-value-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Order ($)</label>
                  <input type="number" step="0.01" min="0" value={promoForm.min_order}
                    onChange={(e) => setPromoForm({...promoForm, min_order: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="promo-min-order" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (0=unlimited)</label>
                  <input type="number" min="0" value={promoForm.max_uses}
                    onChange={(e) => setPromoForm({...promoForm, max_uses: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="promo-max-uses" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expires</label>
                  <input type="datetime-local" value={promoForm.expires_at}
                    onChange={(e) => setPromoForm({...promoForm, expires_at: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="promo-expires" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={promoForm.one_per_customer}
                      onChange={(e) => setPromoForm({...promoForm, one_per_customer: e.target.checked})}
                      className="rounded" data-testid="promo-one-per-customer" />
                    <span className="text-sm text-gray-700">One use per customer</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={promoSaving}
                  className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-50" data-testid="save-promo-btn">
                  {promoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Code'}
                </button>
                <button type="button" onClick={() => setShowPromoForm(false)}
                  className="px-5 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          )}

          {promoLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : promoCodes.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
              <Tag className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <h3 className="font-bold text-gray-900 mb-1">No Promo Codes</h3>
              <p className="text-sm text-gray-500">Create your first code above</p>
            </div>
          ) : (
            <div className="space-y-2">
              {promoCodes.map(pc => (
                <div key={pc.id} className={`bg-white border rounded-lg p-4 flex items-center justify-between ${pc.is_active ? 'border-green-200' : 'border-gray-200'}`}
                  data-testid={`promo-row-${pc.code}`}>
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 text-sm tracking-wider">{pc.code}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded ${pc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {pc.is_active ? 'Active' : 'Off'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {pc.discount_type === 'percentage' ? `${pc.discount_value}% off` : `$${pc.discount_value} off`}
                        {pc.min_order > 0 && ` · Min $${pc.min_order}`}
                        {pc.max_uses > 0 && ` · ${pc.used_count}/${pc.max_uses} used`}
                        {pc.max_uses === 0 && ` · ${pc.used_count} used`}
                        {pc.one_per_customer && ' · 1 per customer'}
                        {pc.expires_at && ` · Exp: ${new Date(pc.expires_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => togglePromoActive(pc.id, pc.is_active)}
                      className={`p-1.5 rounded transition-colors ${pc.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                      title={pc.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-promo-${pc.code}`}>
                      {pc.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => deletePromo(pc.id)}
                      className="p-1.5 text-red-400 hover:bg-red-50 rounded transition-colors" data-testid={`delete-promo-${pc.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Payments (Square Toggle) ─── */}
      {subTab === 'payments' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="square-config">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Square Payments</h3>
                <p className="text-xs text-gray-500">Toggle between sandbox (testing) and production (live)</p>
              </div>
            </div>

            {squareConfig ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Environment</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {squareConfig.environment === 'production'
                        ? 'Processing real payments with real cards'
                        : 'Test mode — no real charges'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1.5 text-xs font-bold uppercase rounded-full ${
                      squareConfig.environment === 'production'
                        ? 'bg-green-100 text-green-700 border border-green-300'
                        : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                    }`} data-testid="square-env-badge">
                      {squareConfig.environment}
                    </span>
                    <button onClick={toggleSquareEnv} disabled={squareToggling}
                      className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      data-testid="toggle-square-env">
                      {squareToggling ? <Loader2 className="w-4 h-4 animate-spin" /> : `Switch to ${squareConfig.environment === 'production' ? 'Sandbox' : 'Production'}`}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Application ID</p>
                    <p className="font-mono text-gray-700 text-xs">{squareConfig.application_id}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Location ID</p>
                    <p className="font-mono text-gray-700 text-xs">{squareConfig.location_id}</p>
                  </div>
                </div>

                {squareConfig.environment === 'sandbox' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Sandbox mode:</strong> Use test card <span className="font-mono">4111 1111 1111 1111</span>, CVV <span className="font-mono">111</span>, any future expiry.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            )}
          </div>
        </div>
      )}

      {/* ─── Checkout Emails ─── */}
      {subTab === 'checkout-emails' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">{emailsTotal} email{emailsTotal !== 1 ? 's' : ''} collected</p>
            <div className="flex gap-2">
              <button onClick={() => setShowBlastForm(!showBlastForm)}
                className="px-4 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded hover:bg-gray-700 flex items-center gap-1 disabled:opacity-40"
                disabled={emailsTotal === 0}
                data-testid="send-blast-btn">
                <Mail className="w-3 h-3" /> Send Promo Blast
              </button>
              <button onClick={() => { setEmailsLoading(true); fetchCheckoutEmails(emailsPage); }}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
          </div>

          {/* Blast Form */}
          {showBlastForm && (
            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4" data-testid="blast-form">
              <h4 className="font-bold text-gray-900 text-sm">Send Promo Email to All {emailsTotal} Customers</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code</label>
                  <select value={blastForm.promo_code} onChange={(e) => setBlastForm({...blastForm, promo_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="blast-promo-select">
                    <option value="">Select a code...</option>
                    {promoCodes.filter(p => p.is_active).map(p => (
                      <option key={p.id} value={p.code}>{p.code} — {p.discount_type === 'percentage' ? `${p.discount_value}% off` : `$${p.discount_value} off`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headline (e.g. "20% OFF Everything")</label>
                  <input value={blastForm.discount_text} onChange={(e) => setBlastForm({...blastForm, discount_text: e.target.value})}
                    placeholder="20% OFF Everything" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="blast-discount-text" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSendBlast} disabled={blastSending || !blastForm.promo_code || !blastForm.discount_text}
                  className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  data-testid="confirm-blast-btn">
                  {blastSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4" /> Send to {emailsTotal} Customers</>}
                </button>
                <button onClick={() => setShowBlastForm(false)} className="px-5 py-2 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          )}

          {emailsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : checkoutEmails.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
              <Mail className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <h3 className="font-bold text-gray-900 mb-1">No Checkout Emails Yet</h3>
              <p className="text-sm text-gray-500">Emails will be collected when customers check out</p>
            </div>
          ) : (
            <>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="checkout-emails-table">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Email</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Checkouts</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">First Seen</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Last Active</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {checkoutEmails.map(em => (
                      <tr key={em.id} className="hover:bg-gray-50" data-testid={`email-row-${em.email}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{em.email}</td>
                        <td className="px-4 py-3 text-gray-600">{em.name || '—'}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded">{em.checkout_count}</span></td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{new Date(em.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{em.last_checkout ? new Date(em.last_checkout).toLocaleDateString() : '—'}</td>
                        <td className="px-2">
                          <button onClick={async () => { await axios.delete(`${API}/admin/checkout-emails/${em.id}`, { headers }); fetchCheckoutEmails(emailsPage); }}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {emailsTotal > 50 && (
                <div className="flex justify-center gap-2">
                  <button onClick={() => { setEmailsPage(p => Math.max(1, p - 1)); fetchCheckoutEmails(Math.max(1, emailsPage - 1)); }}
                    disabled={emailsPage <= 1} className="px-3 py-1.5 text-xs border rounded disabled:opacity-40">Prev</button>
                  <span className="px-3 py-1.5 text-xs text-gray-500">Page {emailsPage}</span>
                  <button onClick={() => { setEmailsPage(p => p + 1); fetchCheckoutEmails(emailsPage + 1); }}
                    disabled={emailsPage * 50 >= emailsTotal} className="px-3 py-1.5 text-xs border rounded disabled:opacity-40">Next</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};


export default SettingsTab;
